// Track the wall-clock duration of an active video call per session.
//
// Two reasons this is a tiny module rather than a Zustand store:
//   1. Pure side-effect tracking — no UI subscribes to it.
//   2. State lives only between markStart() and markEnd(); restoring across
//      page reloads doesn't make sense (a reload terminates the call).
//
// `markStart` is idempotent — multiple callers can declare "the call has
// begun" without double-resetting the clock. `markEnd` clears the entry
// after returning the duration so a follow-up hangup event doesn't double-
// log.

import type { SessionId } from '@prakash-software/shared';

const starts = new Map<string, number>();

export function markStart(sessionId: SessionId, at: number = Date.now()): void {
  if (!starts.has(sessionId)) starts.set(sessionId, at);
}

/** Returns elapsed ms since markStart for this session, or null if there
 *  was no recorded start (race / missed event). Always clears the entry. */
export function markEnd(sessionId: SessionId, at: number = Date.now()): number | null {
  const start = starts.get(sessionId);
  starts.delete(sessionId);
  if (start === undefined) return null;
  return Math.max(0, at - start);
}

export function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
